import { GuideStep } from "../Guide";
import {
  FlowScene,
  FolderScene,
  ClustersScene,
  TrainScene,
  TableScene,
  SidebarScene,
} from "../art/scenes";
import { PropagateScene } from "views/components/LabelPropagation/guides/art/SeedScenes";

export const WELCOME_GUIDE_STEPS: GuideStep[] = [
  {
    title: "Welcome to Visual Layer for Camtek",
    body: (
      <>
        <p>
          Visual Layer turns the images your AOI machines produce into labeled
          training data, with far less manual annotation. The workflow has three
          phases:
        </p>
        <ol>
          <li>
            <strong>Create a dataset</strong> from AOI output folders.
          </li>
          <li>
            <strong>Propagate labels</strong>: seed a few examples, review small
            batches, let the system label the rest.
          </li>
          <li>
            <strong>Run Model Jobs</strong>: send the labeled data to Camtek
            training, then validate the model.
          </li>
        </ol>
      </>
    ),
    tip: "Click the ? next to any page title to reopen its guide. Quick start reopens this tour.",
    art: <FlowScene />,
  },
  {
    title: "1. Create a dataset",
    body: (
      <>
        <p>
          From the Dataset Inventory click <strong>New Dataset</strong>, then
          choose a source: <strong>Import from Scan Result</strong> to pick
          wafer scan results from the Camtek DataServer, or{" "}
          <strong>Upload folder</strong> to enter the names of AOI output
          folders. Each folder must contain <strong>DB.csv</strong>,{" "}
          <strong>metadata.json</strong>, <strong>classes.json</strong> and an{" "}
          <strong>images</strong> folder.
        </p>
        <ul>
          <li>
            Visual Layer validates every folder and shows why one is invalid.
          </li>
          <li>
            All folders in one dataset must share the same Job, Setup, Recipe
            and class list. Incompatible folders are greyed out.
          </li>
          <li>
            Labels in the OClass column of DB.csv are imported as User Labels.
          </li>
        </ul>
      </>
    ),
    tip: "An 18,000 image dataset can take a couple of hours. Leave the page and follow progress in the Task Manager.",
    art: <FolderScene />,
  },
  {
    title: "2. Explore your data",
    body: (
      <>
        <p>
          A Ready dataset opens on the <strong>Explore</strong> tab, where
          images are grouped into clusters of visually similar content.
        </p>
        <ul>
          <li>
            Use <strong>filters</strong> (folders, labels, quality issues) and{" "}
            <strong>search</strong> by text or by image.
          </li>
          <li>
            The <strong>Insights Panel</strong> on the right summarizes labels,
            issues and tags for the current view.
          </li>
          <li>
            Check Issues, Class Outliers and Mislabels before labeling to spot
            unreliable AOI classifications.
          </li>
        </ul>
      </>
    ),
    tip: "Every dataset has its own guide behind the ? in the header, covering each control in detail.",
    art: <ClustersScene />,
  },
  {
    title: "3. Propagate labels",
    body: (
      <>
        <p>
          Open the <strong>Label Propagation</strong> tab in the right panel.
        </p>
        <ul>
          <li>
            Choose at least two labels from your class list and give each one at
            least 5 seed images. Seeds lock once you start.
          </li>
          <li>
            The system labels what it is confident about and sends you small
            review batches. Approve, reassign, ignore or decline, then Submit.
          </li>
          <li>
            It finishes on its own once confident, usually after reviewing only
            5 to 10% of the dataset.
          </li>
        </ul>
      </>
    ),
    tip: "Train-Worthy Goal: 200 labeled images per label by default (your Admin can change it). Reaching it unlocks training.",
    art: <PropagateScene />,
  },
  {
    title: "4. Run Model Jobs",
    body: (
      <>
        <p>
          Click <strong>Model Jobs</strong> in the dataset header.
        </p>
        <ul>
          <li>
            <strong>Train</strong> packages the propagated labels and sends them
            to the Camtek training infrastructure. It needs a completed Label
            Propagation cycle and Train-Worthy status.
          </li>
          <li>
            <strong>Validate</strong> runs a trained model on this dataset and
            compares its predictions with User Labels, propagated labels, or
            none.
          </li>
          <li>
            Progress shows next to the button and in the Task Manager. One
            training or validation job per dataset at a time.
          </li>
        </ul>
      </>
    ),
    tip: "Only Label Propagation labels are sent to training. User tags and imported AOI labels are not.",
    art: <TrainScene />,
  },
  {
    title: "5. Review results in the Model Catalog",
    body: (
      <>
        <p>
          Successful jobs appear in the <strong>Model Catalog</strong> under
          Training Jobs or Validation Jobs.
        </p>
        <ul>
          <li>
            <strong>Results</strong> opens the report: confusion matrix,
            precision, recall and F1 per class, plus per-image confidence.
          </li>
          <li>
            <strong>Export</strong> downloads the whole results folder as a ZIP
            that works offline.
          </li>
          <li>
            The Admin marks the model to deploy with the{" "}
            <strong>Production</strong> checkbox.
          </li>
        </ul>
      </>
    ),
    tip: "Failed jobs are not listed here. Find them, with their error, in the Task Manager.",
    art: <TableScene />,
  },
  {
    title: "Find your way around",
    body: (
      <ul>
        <li>
          <strong>Datasets</strong>: your home page and the start of every
          workflow.
        </li>
        <li>
          <strong>Task Manager</strong>: every running and finished operation
          from the last 14 days.
        </li>
        <li>
          <strong>Model Catalog</strong>: training and validation results.
        </li>
        <li>
          <strong>Settings</strong> (Admin): users, roles and the Train-Worthy
          threshold.
        </li>
        <li>
          <strong>Avatar</strong> at the bottom: your profile and Log Out.
        </li>
      </ul>
    ),
    tip: "Use Quick start next to the page title to see this tour again.",
    art: <SidebarScene />,
  },
];
